import React from 'react';
import { StandardTableActions } from '../../../components/ui';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import type { KnowledgeBaseCategory } from '../useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { Badge } from '../../../components/ui';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export const moduleConfig = {
  module: {
    title: 'KnowledgeBase Categories',
    description: 'Manage knowledge base categories',
    permissionModuleKey: 'knowledge_base',
    route: {
      list: '/knowledge-base/categories',
      new: '/knowledge-base/categories/new',
      detail: (id: number) => `/knowledge-base/categories/${id}`,
      edit: (id: number) => `/knowledge-base/categories/${id}/edit`,
    },
    api: {
      list: '/knowledge-base/categories',
      detail: (id: number) => `/knowledge-base/categories/${id}`,
    },
  },

  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  search: {
    placeholder: 'Search categories by name...',
  },

  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name', 'isActive'],
    },
  ],

  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: KnowledgeBaseCategory) => item.name,
        options: [],
      },
      {
        key: 'isActive',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: KnowledgeBaseCategory) => item.isActive ? 'Active' : 'Inactive',
        options: ['Active', 'Inactive'],
      },
    ],
    applyFilter: (
      items: KnowledgeBaseCategory[],
      filters: {
        name?: string[] | string;
        isActive?: string[];
      }
    ) => {
      return items.filter((item) => {
        // Name filter
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.isActive && filters.isActive.length > 0) {
          const status = item.isActive ? 'Active' : 'Inactive';
          if (!filters.isActive.includes(status)) return false;
        }

        return true;
      });
    },
  },

  export: {
    enabled: true,
    formats: ['csv', 'excel', 'pdf'],
  },

  detail: {
    subtitle: 'Category Details',
    notFoundTitle: 'Category not found',
    notFoundMessage: 'The category you are looking for does not exist or has been deleted.',
    notFoundBackText: 'Back to Categories',
    defaultTitle: 'Category Details',
    getTitle: (item: KnowledgeBaseCategory) => item.name,
  },

  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter category name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'parentId',
      label: 'Parent Category',
      type: 'select',
      placeholder: 'None (top-level category)',
      description: 'Optional. Select a parent to make this a subcategory.',
      options: [], // Populated in Form with useKnowledgeBaseCategories
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter category description',
      rows: 3,
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'icon',
      label: 'Icon',
      type: 'text',
      placeholder: 'Enter icon emoji or name (e.g., 📚, 🎯)',
      description: 'Icon emoji or icon name to display with the category',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'color',
      label: 'Color',
      type: 'color',
      placeholder: 'Select a color',
      description: 'Hex color code for the category',
    },
    {
      name: 'displayOrder',
      label: 'Display Order',
      type: 'number',
      placeholder: '0',
      description: 'Order in which categories appear (lower numbers first)',
      defaultValue: 0,
      validation: {
        min: 0,
      },
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Category is active',
      description: 'Inactive categories will not appear in selection lists',
      labelPosition: 'left',
      defaultValue: true,
    },
  ] as FieldConfig[],

  toolbar: {
    customButton: {
      visible: false,
      icon: '⚙️',
      title: 'Custom Action',
    },
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Category',
        showTitle: false,
        variant: 'primary' as const,
        href: '/knowledge-base/categories/new',
      },
    ],
  },

  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: KnowledgeBaseCategory) => {
      window.location.href = `/knowledge-base/categories/${item.id}`;
    },
    keyExtractor: (item: KnowledgeBaseCategory) => item.id,
  },

  emptyStates: {
    noData: 'No categories found. Create your first category to get started.',
    noSearchResults: 'No categories match your search criteria.',
    noFilterResults: 'No categories match your filter criteria.',
  },

  delete: {
    title: 'Delete Category',
    message: 'Are you sure you want to delete this category? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete category',
    handler: async (id: number) => {
      const { deleteKnowledgeBaseCategory } = await import('./useData');
      await deleteKnowledgeBaseCategory(id);
    },
  },

  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  form: {
    new: {
      title: 'New Category',
      subtitle: 'Create a new knowledge base category',
      submitText: 'Create Category',
    },
    edit: {
      title: 'Edit Category',
      submitText: 'Update Category',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading category...',
    errorMessage: 'Failed to save category. Please try again.',
    create: async (data: Partial<KnowledgeBaseCategory>) => {
      const { createKnowledgeBaseCategory } = await import('./useData');
      const rawParentId = data.parentId as string | number | null | undefined;
      const payload = {
        ...data,
        parentId: rawParentId === '' || rawParentId == null ? undefined : Number(rawParentId),
      };
      return await createKnowledgeBaseCategory(payload);
    },
    update: async (id: number, data: Partial<KnowledgeBaseCategory>) => {
      const { updateKnowledgeBaseCategory } = await import('./useData');
      const rawParentId = data.parentId as string | number | null | undefined;
      const payload = {
        ...data,
        parentId: rawParentId === '' || rawParentId == null ? undefined : Number(rawParentId),
      };
      return await updateKnowledgeBaseCategory(id, payload);
    },
  },
};

export const getColumns = (
  formatDate: (dateString?: string) => string,
  _opts?: unknown,
  colors?: ThemeColors
): Column<KnowledgeBaseCategory>[] => [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value, row) => (
      <div className="flex items-center gap-2">
        {row.parentId && <span className={cn(colors?.textMuted ?? 'text-gray-400')} style={{ marginLeft: '0.5rem' }}>↳</span>}
        {row.icon && <span className="text-lg">{row.icon}</span>}
        <Link
          to={moduleConfig.module.route.detail(row.id)}
          className={colors ? cn(colors.primaryFg, colors.interactiveHover, 'font-medium') : 'text-indigo-600 hover:text-indigo-900 font-medium'}
        >
          {value}
        </Link>
      </div>
    ),
  },
  {
    key: 'parent',
    header: 'Parent',
    sortable: false,
    render: (_value, row) => (
      <span className={cn(colors?.textSecondary ?? 'text-gray-600')}>
        {row.parent ? row.parent.name : '—'}
      </span>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    sortable: false,
    render: (value) => <span className={cn(colors?.textSecondary ?? 'text-gray-600')}>{value || '-'}</span>,
  },
  {
    key: 'color',
    header: 'Color',
    sortable: false,
    render: (value) => {
      if (value) {
        return (
          <div className="flex items-center gap-2">
            <div
              className={cn('w-6 h-6 rounded border', colors?.border ?? 'border-gray-300')}
              style={{ backgroundColor: value }}
            />
            <span className={cn('text-sm', colors?.textSecondary ?? 'text-gray-600')}>{value}</span>
          </div>
        );
      }
      return <span className={cn(colors?.textMuted ?? 'text-gray-400')}>-</span>;
    },
  },
  {
    key: 'displayOrder',
    header: 'Order',
    sortable: true,
    render: (value) => <span className={cn(colors?.textSecondary ?? 'text-gray-600')}>{value || 0}</span>,
  },
  {
    key: 'isActive',
    header: 'Status',
    sortable: true,
    render: (value) => (
      <Badge variant={value ? 'accent' : 'secondary'}>
        {value ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: '_count',
    header: 'Articles',
    sortable: false,
    render: (value, row) => {
      const count = (row as any)._count?.articles || 0;
      return <span className={cn(colors?.textSecondary ?? 'text-gray-600')}>{count}</span>;
    },
  },
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: KnowledgeBaseCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getDetailSections = (
  item: KnowledgeBaseCategory,
  formatDate: (dateString?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  _mutate?: () => void
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Name',
          value: item.name,
        },
        {
          label: 'Parent Category',
          value: item.parent ? item.parent.name : 'None (top-level)',
        },
        {
          label: 'Description',
          value: item.description || 'No description',
        },
        {
          label: 'Icon',
          value: item.icon ? (
            <div className="flex items-center gap-2">
              <span className="text-2xl">{item.icon}</span>
              <span>{item.icon}</span>
            </div>
          ) : (
            'No icon'
          ),
        },
        {
          label: 'Color',
          value: item.color ? (
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: item.color }}
              />
              <span>{item.color}</span>
            </div>
          ) : (
            'No color'
          ),
        },
        {
          label: 'Display Order',
          value: item.displayOrder || 0,
        },
        {
          label: 'Status',
          value: (
            <Badge variant={item.isActive ? 'accent' : 'secondary'}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Badge>
          ),
        },
        {
          label: 'Articles Count',
          value: (item as any)._count?.articles || 0,
        },
      ],
    },
    {
      title: 'Metadata',
      icon: 'ℹ️',
      fields: [
        {
          label: 'Created',
          value: formatDate(item.createdAt),
        },
        {
          label: 'Updated',
          value: formatDate(item.updatedAt),
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: KnowledgeBaseCategory) => {
  return (
    <Badge variant={item.isActive ? 'accent' : 'secondary'}>
      {item.isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
};

// Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
export const formLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'basic',
      title: 'Basic Information',
      icon: '📋',
      gridCols: 'md:grid-cols-12',
      fields: ['name', 'parentId', 'description'],
      fieldSpans: {
        name: 'md:col-span-6',
        parentId: 'md:col-span-6',
        description: 'md:col-span-12',
      },
      collapsible: false,
    },
    {
      id: 'appearance',
      title: 'Appearance',
      icon: '🎨',
      gridCols: 'md:grid-cols-2',
      fields: ['icon', 'color', 'displayOrder'],
      collapsible: true,
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: '⚙️',
      gridCols: 'md:grid-cols-2',
      fields: ['isActive'],
      collapsible: true,
    },
  ],
};

export const getInitialFormData = (): Partial<KnowledgeBaseCategory> => ({
  name: '',
  parentId: '' as any,
  description: '',
  icon: '',
  color: '#3B82F6',
  displayOrder: 0,
  isActive: true,
});

export const mapItemToFormData = (item: KnowledgeBaseCategory): Partial<KnowledgeBaseCategory> => ({
  name: item.name,
  parentId: (item.parentId != null ? item.parentId.toString() : '') as any,
  description: item.description || '',
  icon: item.icon || '',
  color: item.color || '#3B82F6',
  displayOrder: item.displayOrder || 0,
  isActive: item.isActive,
});
